import { useEffect, useState } from "react";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import WebsiteImageField from "./WebsiteImageField";

const ITEM_SCHEMAS = {
  staff: {
    key: "members",
    label: "Staff member",
    fields: [
      ["name", "Name", "text", true],
      ["role", "Role", "text"],
      ["bio", "Bio", "textarea"],
      ["image_url", "Photo", "image"],
      ["image_alt", "Photo description", "text"],
      ["image_decorative", "Photo is decorative", "boolean"],
    ],
    empty: { name: "", role: "", bio: "", image_url: "" },
  },
  sponsors: {
    key: "items",
    label: "Sponsor",
    fields: [
      ["name", "Name", "text", true],
      ["logo_url", "Logo", "image", true],
      ["logo_alt", "Logo description", "text"],
      ["logo_decorative", "Logo is decorative", "boolean"],
      ["url", "Website URL", "https"],
    ],
    empty: { name: "", logo_url: "", url: "" },
  },
  testimonials: {
    key: "items",
    label: "Testimonial",
    fields: [
      ["quote", "Quote", "textarea", true],
      ["attribution", "Attribution", "text", true],
    ],
    empty: { quote: "", attribution: "" },
  },
  faq: {
    key: "items",
    label: "Question",
    fields: [
      ["question", "Question", "text", true],
      ["answer", "Answer", "textarea", true],
    ],
    empty: { question: "", answer: "" },
  },
  navigation: {
    key: "items",
    label: "Navigation link",
    fields: [
      ["label", "Label", "text", true],
      ["url", "URL", "link", true],
    ],
    empty: { label: "", url: "" },
  },
  footer: {
    key: "links",
    label: "Footer link",
    fields: [
      ["label", "Label", "text", true],
      ["url", "URL", "link", true],
    ],
    empty: { label: "", url: "" },
  },
};

export const BLOCK_DEFINITIONS = {
  hero: {
    label: "Hero",
    fields: [
      ["headline", "Headline", "text"],
      ["subheadline", "Subheadline", "textarea"],
      ["image_url", "Photo", "image"],
      ["image_alt", "Photo description", "text"],
      ["image_decorative", "Photo is decorative", "boolean"],
    ],
  },
  rich_text: {
    label: "Rich text",
    fields: [["content", "Content", "textarea"]],
  },
  image_text: {
    label: "Image and text",
    fields: [
      ["heading", "Heading", "text"],
      ["body", "Body", "textarea"],
      ["image_url", "Photo", "image"],
      ["alt", "Image description", "text"],
    ],
  },
  cta: {
    label: "Call to action",
    fields: [
      ["heading", "Heading", "text"],
      ["label", "Button label", "text"],
      ["url", "Button URL", "link"],
    ],
  },
  program_grid: {
    label: "Program grid",
    fields: [
      ["heading", "Heading", "text"],
      ["program_ids", "Programs", "programs"],
    ],
  },
  featured_program: {
    label: "Featured program",
    fields: [
      ["heading", "Heading", "text"],
      ["program_id", "Program", "program"],
    ],
  },
  upcoming_events: {
    label: "Upcoming events",
    fields: [
      ["heading", "Heading", "text"],
      ["program_ids", "Programs", "programs"],
    ],
  },
  staff: {
    label: "Staff",
    fields: [
      ["heading", "Heading", "text"],
      ["members", "Staff members", "items"],
    ],
  },
  sponsors: {
    label: "Sponsors",
    fields: [
      ["heading", "Heading", "text"],
      ["items", "Sponsors", "items"],
    ],
  },
  testimonials: {
    label: "Testimonials",
    fields: [
      ["heading", "Heading", "text"],
      ["items", "Testimonials", "items"],
    ],
  },
  faq: {
    label: "FAQ",
    fields: [
      ["heading", "Heading", "text"],
      ["items", "Questions and answers", "items"],
    ],
  },
  contact_form: {
    label: "Contact form",
    fields: [
      ["heading", "Heading", "text"],
      ["recipient_label", "Recipient label", "text"],
      ["consent_text", "Consent text", "textarea"],
      ["notify_by_email", "Email notifications", "boolean"],
    ],
  },
  location_contact: {
    label: "Location and contact",
    fields: [
      ["heading", "Heading", "text"],
      ["body", "Body", "textarea"],
      ["show_public_address", "Show approved public address", "boolean"],
    ],
  },
  registration_cta: {
    label: "Registration call to action",
    fields: [
      ["label", "Button label", "text"],
      ["program_id", "Program", "program"],
    ],
  },
  navigation: {
    label: "Navigation",
    fields: [["items", "Navigation links", "items"]],
  },
  footer: {
    label: "Footer",
    fields: [
      ["text", "Footer text", "textarea"],
      ["links", "Footer links", "items"],
    ],
  },
};

export const starterProps = (type, programs) => {
  const firstProgram = programs[0]?.program_id;
  const values = {
    hero: {
      headline: "A place to play. A community to belong to.",
      subheadline: "Programs built for every stage of the game.",
    },
    rich_text: {
      content: "Tell families what makes your organization special.",
    },
    image_text: {
      heading: "Built for the community",
      body: "Add your story.",
      image_url: "https://app.sporthouse.ai/brand/sporthouse-footer-logo.png",
      alt: "Sporthouse organization",
    },
    cta: {
      heading: "Ready to play?",
      label: "Explore programs",
      url: "/programs",
    },
    program_grid: {
      heading: "Upcoming programs",
      program_ids: firstProgram ? [firstProgram] : [],
    },
    featured_program: firstProgram
      ? { heading: "Featured program", program_id: firstProgram }
      : null,
    upcoming_events: {
      heading: "Upcoming events",
      program_ids: firstProgram ? [firstProgram] : [],
    },
    staff: {
      heading: "Our staff",
      members: [{ name: "Coach name", role: "Coach" }],
    },
    sponsors: {
      heading: "Our sponsors",
      items: [
        {
          name: "Community partner",
          logo_url:
            "https://app.sporthouse.ai/brand/sporthouse-footer-logo.png",
          logo_alt: "Sporthouse community partner logo",
        },
      ],
    },
    testimonials: {
      heading: "Community stories",
      items: [
        {
          quote: "A welcoming place to play.",
          attribution: "Sporthouse family",
        },
      ],
    },
    faq: {
      heading: "Questions",
      items: [
        {
          question: "How do I register?",
          answer:
            "Choose an open program and follow the secure registration link.",
        },
      ],
    },
    contact_form: {
      heading: "Contact us",
      recipient_label: "Organization staff",
      notify_by_email: true,
    },
    location_contact: {
      heading: "Find us",
      body: "Public contact information comes from your approved settings.",
      show_public_address: false,
    },
    registration_cta: firstProgram
      ? { label: "Register", program_id: firstProgram }
      : null,
    navigation: {
      items: [
        { label: "Home", url: "/" },
        { label: "Programs", url: "/programs" },
      ],
    },
    footer: { text: "Built with Sporthouse", links: [] },
  };
  return values[type];
};

const WebsiteBlockEditor = ({
  block,
  programs,
  error,
  onSave,
  onDirtyChange,
  onUploadStateChange,
  onAssetUploaded,
}) => {
  const [values, setValues] = useState(block.props);
  const [fieldErrors, setFieldErrors] = useState({});
  const definition = BLOCK_DEFINITIONS[block.type];
  useEffect(() => {
    setValues(block.props);
    onDirtyChange?.(false);
  }, [block.block_id, block.revision, block.props, onDirtyChange]);

  const update = (key, value) => {
    onDirtyChange?.(true);
    setValues((current) => ({ ...current, [key]: value }));
  };
  const itemSchema = ITEM_SCHEMAS[block.type];
  const updateItem = (index, key, value) => {
    const items = [...(values[itemSchema.key] || [])];
    items[index] = { ...items[index], [key]: value };
    update(itemSchema.key, items);
  };
  const updateItemFields = (index, updates) => {
    const items = [...(values[itemSchema.key] || [])];
    items[index] = { ...items[index], ...updates };
    update(itemSchema.key, items);
  };
  const moveItem = (index, delta) => {
    const items = [...(values[itemSchema.key] || [])];
    const target = index + delta;
    if (target < 0 || target >= items.length) return;
    [items[index], items[target]] = [items[target], items[index]];
    update(itemSchema.key, items);
  };
  const validate = () => {
    const next = {};
    definition.fields.forEach(([key, label, kind]) => {
      const value = values[key];
      if (kind === "link" && value && !/^(https:\/\/|\/)/.test(value))
        next[key] = `${label} must start with https:// or /.`;
      if (["url", "image"].includes(kind) && value && !/^https:\/\//.test(value))
        next[key] = `${label} must start with https://.`;
    });
    if (
      values.image_url &&
      !values.image_decorative &&
      !(values.image_alt || values.alt || "").trim()
    )
      next.image_alt = "Photo description is required unless the photo is decorative.";
    if (itemSchema) {
      (values[itemSchema.key] || []).forEach((item, index) => {
        itemSchema.fields.forEach(([key, label, kind, required]) => {
          const value = item[key]?.trim?.() || "";
          const path = `${itemSchema.key}.${index}.${key}`;
          if (required && !value) next[path] = `${label} is required.`;
          else if (kind === "link" && value && !/^(https:\/\/|\/)/.test(value))
            next[path] = `${label} must start with https:// or /.`;
          else if (["https", "image"].includes(kind) && value && !/^https:\/\//.test(value))
            next[path] = `${label} must start with https://.`;
        });
        const imageUrl = item.image_url || item.logo_url;
        const decorative = item.image_decorative || item.logo_decorative;
        const description = item.image_alt || item.logo_alt || "";
        if (imageUrl && !decorative && !description.trim()) {
          const descriptionKey = item.logo_url ? "logo_alt" : "image_alt";
          next[`${itemSchema.key}.${index}.${descriptionKey}`] =
            `${item.logo_url ? "Logo" : "Photo"} description is required unless it is decorative.`;
        }
      });
    }
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  };
  const submit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    const cleaned = { ...values };
    if (itemSchema) {
      cleaned[itemSchema.key] = (values[itemSchema.key] || []).map((item) =>
        Object.fromEntries(
          Object.entries(item).filter(([, value]) => value !== ""),
        ),
      );
    }
    const saved = await onSave(cleaned);
    if (saved !== false) onDirtyChange?.(false);
  };
  return (
    <form
      id={`website-block-form-${block.block_id}`}
      className="mt-3 space-y-5 border-t border-[#e2e8e2] bg-[#fbfcf8] px-1 pt-5"
      onSubmit={submit}
    >
      {definition.fields.map(([key, label, kind]) => {
        const id = `${key}-${block.block_id}`;
        const describedBy = fieldErrors[key]
          ? `error-${block.block_id}-${key}`
          : error
            ? `error-${block.block_id}`
            : undefined;
        if (kind === "items") {
          const items = values[key] || [];
          return (
            <fieldset key={key} className="space-y-3">
              <legend className="text-sm font-semibold">{label}</legend>
              {items.map((item, index) => (
                <div key={index} className="rounded-2xl border border-[#dce4dc] bg-white p-4">
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <strong className="text-sm">
                      {itemSchema.label} {index + 1}
                    </strong>
                    <div className="flex gap-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => moveItem(index, -1)}
                        disabled={index === 0}
                        aria-label={`Move ${itemSchema.label} ${index + 1} up`}
                      >
                        Up
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => moveItem(index, 1)}
                        disabled={index === items.length - 1}
                        aria-label={`Move ${itemSchema.label} ${index + 1} down`}
                      >
                        Down
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          update(
                            key,
                            items.filter((_, itemIndex) => itemIndex !== index),
                          )
                        }
                        aria-label={`Remove ${itemSchema.label} ${index + 1}`}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {itemSchema.fields.map(
                      ([itemKey, itemLabel, itemKind, required]) => {
                        const itemId = `${key}-${index}-${itemKey}-${block.block_id}`;
                        const errorKey = `${key}.${index}.${itemKey}`;
                        if (itemKind === "image")
                          return (
                            <WebsiteImageField
                              key={itemKey}
                              id={itemId}
                              label={`${itemSchema.label} ${index + 1} ${itemLabel.toLowerCase()}`}
                              value={item[itemKey] || ""}
                              required={required}
                              validationError={fieldErrors[errorKey]}
                              onUploadStateChange={onUploadStateChange}
                              onAssetUploaded={onAssetUploaded}
                              onChange={(value, assetId) => {
                                updateItemFields(index, {
                                  [itemKey]: value,
                                  [itemKey === "logo_url" ? "logo_asset_id" : "image_asset_id"]:
                                    assetId || "",
                                });
                              }}
                            />
                          );
                        if (itemKind === "boolean")
                          return (
                            <label key={itemKey} className="flex min-h-11 items-center gap-3 text-sm font-semibold">
                              <input
                                type="checkbox"
                                checked={Boolean(item[itemKey])}
                                onChange={(event) =>
                                  updateItem(index, itemKey, event.target.checked)
                                }
                              />
                              {itemSchema.label} {index + 1} {itemLabel.toLowerCase()}
                            </label>
                          );
                        const Control =
                          itemKind === "textarea" ? "textarea" : Input;
                        return (
                          <label
                            key={itemKey}
                            className="block text-sm font-semibold"
                            htmlFor={itemId}
                          >
                            {itemSchema.label} {index + 1} {itemLabel.toLowerCase()}
                            {required ? " (required)" : ""}
                            <Control
                              id={itemId}
                              type="text"
                              value={item[itemKey] || ""}
                              onChange={(event) =>
                                updateItem(index, itemKey, event.target.value)
                              }
                              className={`mt-2 w-full rounded-md border p-3 font-normal ${itemKind === "textarea" ? "min-h-20" : ""}`}
                              aria-invalid={Boolean(fieldErrors[errorKey])}
                              aria-describedby={
                                fieldErrors[errorKey]
                                  ? `error-${block.block_id}-${errorKey}`
                                  : undefined
                              }
                            />
                            {fieldErrors[errorKey] && (
                              <span
                                id={`error-${block.block_id}-${errorKey}`}
                                className="mt-1 block text-sm text-red-700"
                              >
                                {fieldErrors[errorKey]}
                              </span>
                            )}
                          </label>
                        );
                      },
                    )}
                  </div>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                onClick={() => update(key, [...items, { ...itemSchema.empty }])}
              >
                Add {itemSchema.label.toLowerCase()}
              </Button>
            </fieldset>
          );
        }
        if (kind === "program")
          return (
            <label
              key={key}
              className="block text-sm font-semibold"
              htmlFor={id}
            >
              {label}
              <select
                id={id}
                value={values[key] || ""}
                onChange={(event) => update(key, event.target.value)}
                className="mt-2 h-11 w-full rounded-md border px-3"
                aria-describedby={describedBy}
              >
                <option value="">Select a program</option>
                {programs.map((program) => (
                  <option key={program.program_id} value={program.program_id}>
                    {program.name}
                  </option>
                ))}
              </select>
            </label>
          );
        if (kind === "image")
          return (
            <WebsiteImageField
              key={key}
              id={id}
              label={label}
              value={values[key] || ""}
              validationError={fieldErrors[key]}
              onUploadStateChange={onUploadStateChange}
              onAssetUploaded={onAssetUploaded}
              onChange={(value, assetId) => {
                update(key, value);
                update("image_asset_id", assetId || "");
              }}
            />
          );
        if (kind === "programs")
          return (
            <fieldset key={key}>
              <legend className="text-sm font-semibold">{label}</legend>
              <div className="mt-2 max-h-40 space-y-2 overflow-auto rounded-lg border p-3">
                {programs.length ? (
                  programs.map((program) => (
                    <label
                      key={program.program_id}
                      className="flex min-h-11 items-center gap-3 text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={(values[key] || []).includes(
                          program.program_id,
                        )}
                        onChange={(event) =>
                          update(
                            key,
                            event.target.checked
                              ? [...(values[key] || []), program.program_id]
                              : (values[key] || []).filter(
                                  (id) => id !== program.program_id,
                                ),
                          )
                        }
                      />
                      {program.name}
                    </label>
                  ))
                ) : (
                  <p className="text-[#60706a]">No programs are available.</p>
                )}
              </div>
            </fieldset>
          );
        if (kind === "boolean")
          return (
            <label
              key={key}
              className="flex min-h-11 items-center gap-3 text-sm font-semibold"
            >
              <input
                type="checkbox"
                checked={Boolean(values[key])}
                onChange={(event) => update(key, event.target.checked)}
              />
              {label}
            </label>
          );
        if (kind === "textarea")
          return (
            <label
              key={key}
              className="block text-sm font-semibold"
              htmlFor={id}
            >
              {label}
              <textarea
                id={id}
                value={values[key] || ""}
                onChange={(event) => update(key, event.target.value)}
                className="mt-2 min-h-24 w-full rounded-md border p-3 font-normal"
                aria-describedby={describedBy}
              />
            </label>
          );
        return (
          <label key={key} className="block text-sm font-semibold" htmlFor={id}>
            {label}
            <Input
              id={id}
              type="text"
              value={values[key] || ""}
              onChange={(event) => update(key, event.target.value)}
              className="mt-2"
              aria-describedby={describedBy}
              aria-invalid={Boolean(fieldErrors[key])}
            />
            {fieldErrors[key] && (
              <span
                id={`error-${block.block_id}-${key}`}
                className="mt-1 block text-sm text-red-700"
              >
                {fieldErrors[key]}
              </span>
            )}
          </label>
        );
      })}
      {error && (
        <p id={`error-${block.block_id}`} className="text-sm text-red-700">
          {error}
        </p>
      )}
      <div className="sticky bottom-0 -mx-1 border-t border-[#e2e8e2] bg-[#fbfcf8]/95 px-1 py-3 backdrop-blur"><Button type="submit" className="w-full rounded-full bg-[#102a27] text-white hover:bg-[#173b36]">Save block</Button></div>
    </form>
  );
};

export default WebsiteBlockEditor;
